import { useTranslation } from 'react-i18next';
import { Waypoints } from 'lucide-react';
import { ProcessFormFields } from '@/components/processes/ProcessFormFields';
import {
  AddProcessRequestSchema,
  type AddProcessRequest,
  type ProcessInput,
  type ProcessListRow
} from '@bliks-io/contracts';
import { FormDialog } from '@/components/dialogs/FormDialog';

const EMPTY_PROCESS_FORM: AddProcessRequest = {
  owner: '',
  accountable: '',
  name: '',
  description: '',
  department: '',
  apqcEquivalentId: '',
  category: null
};

interface ProcessFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: AddProcessRequest) => unknown;
  /** Existing process to edit — the listing row its only call site holds. */
  process?: ProcessListRow;
  /**
   * Initial data for drafts (already has IDs as strings)
   */
  initialData?: ProcessInput;
  /**
   * Force editing mode (useful when using initialData for draft editing)
   */
  isEditing?: boolean;
  /**
   * When true, automatically populates owner from department manager when department changes.
   * When false (default), user manually selects owner from department users.
   */
  autoPopulateOwnerFromManager?: boolean;
  /** Custom title override. */
  title?: string;
  /** Custom description override. */
  description?: string;
  /**
   * When true, the owner and department fields are disabled (user lacks canEditDepartment capability).
   */
  disableOwnerAndDepartment?: boolean;
  /**
   * When false, hides the optional accountable person field.
   */
  showAccountable?: boolean;
}

/**
 * Form dialog for creating/editing processes
 * Used by both all-processes page (for CRUD) and create-processes page (for drafts)
 */
export function ProcessFormDialog({
  open,
  onOpenChange,
  onSubmit,
  process,
  initialData,
  isEditing: isEditingProp,
  autoPopulateOwnerFromManager = false,
  title: titleProp,
  description: descriptionProp,
  disableOwnerAndDepartment = false,
  showAccountable = true
}: ProcessFormDialogProps) {
  const { t } = useTranslation();

  // Determine editing state: explicit prop > process presence > false
  const isEditing = isEditingProp ?? !!process;

  const defaultValues: AddProcessRequest = (() => {
    if (process) {
      return {
        ...EMPTY_PROCESS_FORM,
        owner: process.owner?._id ?? '',
        accountable: process.accountable?._id || '',
        name: process.name || '',
        description: process.description || '',
        department: process.department?._id ?? '',
        apqcEquivalentId: process.apqcEquivalentId || '',
        category: process.category || null
      };
    }
    if (initialData) {
      return {
        ...EMPTY_PROCESS_FORM,
        ...initialData,
        owner: initialData.owner ?? '',
        accountable: initialData.accountable ?? '',
        category: initialData.category ?? null
      };
    }
    return EMPTY_PROCESS_FORM;
  })();

  const title =
    titleProp ?? (isEditing ? t('processes.editDetails') : t('processes.createProcess'));
  const description =
    descriptionProp ??
    (isEditing ? t('processes.editProcessDescription') : t('processes.createProcessDescription'));

  return (
    <FormDialog<AddProcessRequest>
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      icon={Waypoints}
      schema={AddProcessRequestSchema}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      isEditing={isEditing}
    >
      {({ form }) => (
        <ProcessFormFields
          form={form}
          isEditing={isEditing}
          autoPopulateOwnerFromManager={autoPopulateOwnerFromManager}
          disableOwnerAndDepartment={disableOwnerAndDepartment}
          showAccountable={showAccountable}
        />
      )}
    </FormDialog>
  );
}
